/**
 * Governance — the club's own rules, and the people who apply them.
 *
 * ---------------------------------------------------------------------
 * THE BYLAWS ARE A DOCUMENT, SO THEY ARE LAID OUT AS ONE
 *
 * Not a table of rows. Somebody opening this is either reading an
 * article through or hunting one sentence, and both want the shape the
 * club adopted — Article III, section B, paragraph 4, subparagraph a,
 * indented the way it is on the page. The citation sits beside every
 * section because that is how it gets quoted in an email an hour later.
 *
 * ---------------------------------------------------------------------
 * SEARCH IS THE FRONT DOOR
 *
 * Nobody browses a constitution. They remember four words of a rule and
 * want the rule. So the search box is above the document rather than
 * beside it, and a result is the sentence itself with its citation, not
 * a link to a page they then have to scan.
 */

import { useMemo, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { date as fmtDate } from '../lib/format.ts';
import { BylawNotices, Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

interface Section {
  id: string;
  label: string;
  heading: string | null;
  body: string;
  path: string;
  sections: Section[];
}

interface Article {
  id: string;
  ordinal: number;
  numeral: string;
  title: string;
  body: string | null;
  sections: Section[];
}

// =====================================================================
// The bylaws
// =====================================================================

export function BylawsPage() {
  const { atLeast } = useSession();
  const { data, error, loading, reload } = useApi<{ document: any; articles: Article[] }>(
    '/v1/governance/bylaws',
  );
  const [q, setQ] = useState('');
  const search = useApi<{ results: any[] }>(
    q.trim().length >= 3 ? `/v1/governance/bylaws/search?q=${encodeURIComponent(q.trim())}` : null,
    [q],
  );
  const versions = useApi<{ versions: any[] }>('/v1/governance/bylaws/versions');

  // `loading` alone unmounts this whole page on every refetch — including
  // the refetch an import triggers, which destroyed the card that was
  // about to say the import worked. Only the FIRST load is a blank page.
  if (loading && !data) return <Loading />;

  const doc = data?.document;

  if (!doc) {
    return (
      <>
        <h1>Bylaws</h1>
        <ErrorBanner error={error} />
        <Card title="Nothing Adopted Yet">
          <Empty>
            This club has no governing document on file. Once its constitution is loaded, it can be
            read and searched here, and a rule can be cited from anywhere else in the system.
          </Empty>
        </Card>
        {/* Importing is how the FIRST document arrives, so this has to be
            reachable from the empty state — not only once a club already
            has one. */}
        {atLeast('club_admin') && <BylawsInOut hasDocument={false} onChanged={reload} />}
      </>
    );
  }

  const searching = q.trim().length >= 3;

  return (
    <>
      <div className="row" style={{ marginBottom: 4, alignItems: 'baseline', gap: 12 }}>
        <h1>{doc.title}</h1>
        <span className="muted small">adopted {fmtDate(doc.adopted_on)}</span>
      </div>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 16 }}>
        {(data?.articles ?? []).length} articles.{' '}
        {(versions.data?.versions ?? []).length > 1 &&
          `${versions.data!.versions.length - 1} earlier version${
            versions.data!.versions.length === 2 ? '' : 's'
          } kept — what a rule said before it was amended is usually what an argument is about.`}
      </p>

      <ErrorBanner error={error} />

      <Card title="Find a Rule">
        <input
          placeholder="Four words you remember — “twenty-five voting members”, “wholly forfeited”"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {searching && (
          <div style={{ marginTop: 12 }}>
            {(search.data?.results ?? []).length === 0 ? (
              <Empty>Nothing in the bylaws matches that.</Empty>
            ) : (
              (search.data?.results ?? []).map((r, i) => (
                <div key={i} className="bylaw-hit">
                  <div className="bylaw-cite">
                    {r.path}
                    <span className="muted"> · {r.article_title}</span>
                  </div>
                  {r.heading && <strong>{r.heading}. </strong>}
                  <span>{r.body}</span>
                </div>
              ))
            )}
          </div>
        )}
      </Card>

      {!searching &&
        (data?.articles ?? []).map((a) => (
          <Card key={a.id} title={`Article ${a.numeral} — ${a.title}`}>
            {a.body && <p className="bylaw-body">{a.body}</p>}
            {a.sections.map((s) => (
              <SectionBlock key={s.id} section={s} depth={0} />
            ))}
          </Card>
        ))}

      {!searching && (versions.data?.versions ?? []).length > 1 && (
        <Card title="Earlier Versions">
          <table className="data">
            <thead>
              <tr>
                <th>Adopted</th>
                <th>Title</th>
                <th>Status</th>
                <th>Where it came from</th>
              </tr>
            </thead>
            <tbody>
              {versions.data!.versions.map((v) => (
                <tr key={v.id}>
                  <td>{fmtDate(v.adopted_on)}</td>
                  <td>{v.title}</td>
                  <td>
                    <Status tone={v.status === 'in_force' ? 'good' : 'neutral'}>
                      {v.status.replace(/_/g, ' ')}
                    </Status>
                  </td>
                  <td className="muted small">{v.source_note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {atLeast('club_admin') && <BylawsInOut hasDocument onChanged={reload} />}
    </>
  );
}

/**
 * Taking the document out and putting it back.
 *
 * 0104 built the round trip and nothing could reach it. The shape here is
 * the one that migration argued for and it is worth restating, because it
 * is the whole reason this is safe:
 *
 *   READING IS FREE, WRITING IS NOT. The parser is a best effort at
 *   whatever shape a club's own file happens to be in, and a parser that
 *   will sometimes be wrong should be wrong where somebody can SEE it.
 *   So the first button reads the file and writes nothing — it shows the
 *   outline it made, everything it could not place, and which of the
 *   club's own references would stop resolving. Only then is there a
 *   second button.
 *
 *   IMPORTING NEVER OVERWRITES. It records a new version and moves the
 *   current one to superseded, so the wording that was in force stays
 *   readable. What a rule said before it was amended is what a by-law
 *   argument is actually about.
 */
function BylawsInOut({ hasDocument, onChanged }: { hasDocument: boolean; onChanged: () => void }) {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<any | null>(null);
  const [done, setDone] = useState<any | null>(null);
  const [exported, setExported] = useState<string | null>(null);
  const versions = useApi<{ versions: any[] }>('/v1/governance/bylaws/versions');

  const takeOut = useMutation(async (documentId?: string) => {
    const r = await api<{ text: string }>(
      `/v1/governance/bylaws/export${documentId ? `?document_id=${documentId}` : ''}`,
    );
    setExported(r.text);
    return r;
  });

  const read = useMutation(async () => {
    const r = await api('/v1/governance/bylaws/read', { method: 'POST', body: { text } });
    setPreview(r);
    setDone(null);
    return r;
  });

  const bringIn = useMutation(async () => {
    const r = await api('/v1/governance/bylaws/import', {
      method: 'POST',
      body: { text, confirm: true },
    });
    setDone(r);
    setPreview(null);
    setText('');
    setFileName(null);
    versions.reload();
    onChanged();
    return r;
  });

  function download(name: string, body: string) {
    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  }

  const list = versions.data?.versions ?? [];

  return (
    <Card title="Take It Out, Put It Back">
      <ErrorBanner error={takeOut.error ?? read.error ?? bringIn.error} />

      <p className="muted small" style={{ marginTop: 0 }}>
        Plain text on purpose. Open it in anything, edit it, and send it back — the same headings
        produce the same structure. Reading a file changes nothing; importing records a new
        version and moves the current one to superseded, so nothing is ever overwritten.
      </p>

      {/* ---------------------------------------------------------- */}
      {hasDocument && (
        <div style={{ marginBottom: 22 }}>
          <h3 style={{ marginBottom: 8 }}>Out</h3>
          <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
            <button
              className="secondary"
              disabled={takeOut.busy}
              onClick={() => void takeOut.run(undefined)}
            >
              {takeOut.busy ? 'Fetching…' : 'Export the document in force'}
            </button>
            {exported && (
              <>
                <button
                  className="secondary small"
                  onClick={() => download('bylaws.md', exported)}
                >
                  Save as a file
                </button>
                <span className="muted small">
                  {exported.split('\n').length} lines
                </span>
              </>
            )}
          </div>

          {exported && (
            <textarea
              readOnly
              rows={12}
              value={exported}
              onFocus={(e) => e.currentTarget.select()}
              style={{ width: '100%', marginTop: 10, fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}
            />
          )}

          {list.length > 1 && (
            <div className="muted small" style={{ marginTop: 8 }}>
              Earlier versions:{' '}
              {list
                .filter((v: any) => v.status !== 'in_force')
                .map((v: any, i: number) => (
                  <span key={v.id}>
                    {i > 0 && ', '}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        void takeOut.run(v.id);
                      }}
                    >
                      {fmtDate(v.adopted_on)}
                    </a>
                  </span>
                ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------------------------------------------------- */}
      <h3 style={{ marginBottom: 8 }}>In</h3>

      <div className="row" style={{ gap: 8, alignItems: 'baseline', marginBottom: 8 }}>
        <input
          type="file"
          accept=".txt,.md,.markdown,text/plain"
          style={{ flex: '0 0 auto', width: 'auto' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            // Read in the browser. The file never goes anywhere until
            // somebody presses the button below, and even then what is
            // sent is the text, not the file.
            const r = new FileReader();
            r.onload = () => {
              setText(String(r.result ?? ''));
              setFileName(f.name);
              setPreview(null);
              setDone(null);
            };
            r.readAsText(f);
          }}
        />
        {fileName && <span className="muted small">{fileName}</span>}
      </div>

      <textarea
        rows={10}
        value={text}
        onChange={(e) => { setText(e.target.value); setPreview(null); setDone(null); }}
        placeholder={'…or paste it here.\n\n# Constitution and By-Laws\n\n## Article I. Name\n\n### Section A.\nThe name of the club shall be…'}
        style={{ width: '100%', fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}
      />

      <div className="actions">
        <button disabled={text.trim().length < 20 || read.busy} onClick={() => void read.run()}>
          {read.busy ? 'Reading…' : 'Read it — change nothing'}
        </button>
        {text && (
          <button className="secondary" onClick={() => { setText(''); setFileName(null); setPreview(null); }}>
            Clear
          </button>
        )}
      </div>

      {/* ---------------------------------------------------------- */}
      {preview && (
        <div style={{ marginTop: 18 }}>
          <div className="notice">
            Nothing has been written yet. This is what the software made of that file.
          </div>

          <div className="tiles" style={{ marginBottom: 14 }}>
            <Tile label="Articles" value={String(preview.articles)} />
            <Tile label="Sections" value={String(preview.sections)} />
            <Tile
              label="Could not place"
              value={String(preview.problems?.length ?? 0)}
              tone={(preview.problems?.length ?? 0) > 0 ? 'warning' : 'good'}
              note={(preview.problems?.length ?? 0) > 0 ? 'listed below' : 'all of it read'}
            />
            <Tile
              label="References at risk"
              value={String(preview.would_orphan?.length ?? 0)}
              tone={(preview.would_orphan?.length ?? 0) > 0 ? 'serious' : 'good'}
              note={(preview.would_orphan?.length ?? 0) > 0 ? 'named below' : 'all still resolve'}
            />
          </div>

          <p className="muted small">
            <strong>{preview.title ?? 'Untitled'}</strong>
            {preview.adopted_on && <> · adopted {fmtDate(preview.adopted_on)}</>}
            {preview.would_supersede && (
              <> · would supersede the version adopted {fmtDate(preview.would_supersede.effective_on ?? preview.would_supersede.adopted_on)}</>
            )}
          </p>

          {(preview.problems?.length ?? 0) > 0 && (
            <>
              <h4 style={{ marginBottom: 6 }}>What it could not place</h4>
              <ul className="small">
                {preview.problems.map((p: string, i: number) => <li key={i}>{p}</li>)}
              </ul>
            </>
          )}

          {(preview.would_orphan?.length ?? 0) > 0 && (
            <>
              <h4 style={{ marginBottom: 6 }}>References that would stop resolving</h4>
              {/* Named individually. "3 references would be lost" is not
                  something anybody can act on. */}
              <table className="data" style={{ marginBottom: 12 }}>
                <tbody>
                  {preview.would_orphan.map((o: any, i: number) => (
                    <tr key={i}>
                      <td className="mono small">{o.path}</td>
                      <td className="small">{o.kind === 'notice' ? 'A notice shown in the product' : 'A rule the club adopted'}</td>
                      <td className="small">{o.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <details style={{ marginBottom: 12 }}>
            <summary className="small">The outline it read, in order</summary>
            <table className="data" style={{ marginTop: 8 }}>
              <tbody>
                {preview.outline.map((n: any, i: number) => (
                  <tr key={i}>
                    <td className="mono small" style={{ paddingLeft: n.kind === 'article' ? 0 : 20 }}>
                      {n.path ?? n.label}
                    </td>
                    <td className="small">{n.heading ?? <span className="muted">no heading</span>}</td>
                    <td className="num muted small">{n.words} words</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>

          <div className="actions">
            <button className="danger" disabled={bringIn.busy} onClick={() => void bringIn.run()}>
              {bringIn.busy ? 'Importing…' : 'Import it as a new version'}
            </button>
            <button className="secondary" onClick={() => setPreview(null)}>Not yet</button>
            <span className="muted small">
              The version in force becomes superseded and stays readable.
            </span>
          </div>
        </div>
      )}

      {done && (
        // The column names are the function's, not invented ones:
        // `articles`, `sections`, `superseded`. Guessing them produced a
        // banner that said "Imported.  article and  section." and looked
        // like a rendering bug rather than a wrong field.
        <div className="ok-banner" style={{ marginTop: 16 }}>
          <strong>
            Imported. {done.articles} article{done.articles === 1 ? '' : 's'},{' '}
            {done.sections} section{done.sections === 1 ? '' : 's'}
            {done.superseded ? ', and the version that was in force is now superseded' : ''}.
          </strong>
          {(done.notices_moved > 0 || done.policies_moved > 0) && (
            <div style={{ marginTop: 4 }}>
              {done.notices_moved} notice{done.notices_moved === 1 ? '' : 's'} and{' '}
              {done.policies_moved} rule citation{done.policies_moved === 1 ? '' : 's'} now point
              at the new wording.
            </div>
          )}
          {(done.notices_orphaned > 0 || done.policies_orphaned > 0) && (
            <div style={{ marginTop: 4 }}>
              {done.notices_orphaned + done.policies_orphaned} reference
              {done.notices_orphaned + done.policies_orphaned === 1 ? '' : 's'} no longer
              resolve: {(done.orphaned_paths ?? []).join(', ')}. Nothing was dropped silently —
              they are listed so somebody can point them somewhere.
            </div>
          )}
          {(done.problems?.length ?? 0) > 0 && (
            <div style={{ marginTop: 4 }}>
              {done.problems.length} line{done.problems.length === 1 ? '' : 's'} could not be
              placed and were left out.
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/** A section and everything under it, indented the way the page is. */
function SectionBlock({ section, depth }: { section: Section; depth: number }) {
  return (
    <div className="bylaw-section" style={{ marginLeft: depth * 18 }}>
      <div className="bylaw-cite">{section.path}</div>
      <p className="bylaw-body">
        <strong>
          {section.label}
          {section.heading ? `. ${section.heading}.` : '.'}
        </strong>{' '}
        {section.body}
      </p>
      {section.sections.map((s) => (
        <SectionBlock key={s.id} section={s} depth={depth + 1} />
      ))}
    </div>
  );
}

// =====================================================================
// The board and its committees
// =====================================================================

/**
 * A committee's own controls.
 *
 * Retiring and deleting are deliberately not the same button and do not
 * look alike. Retiring keeps eleven years of who-served-when and can be
 * undone; deleting is for the committee created twenty seconds ago with a
 * typo in its name, and the database refuses it for anything else.
 */
function BodyControls({ body, onChanged }: { body: any; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: body.name as string,
    seat_count: body.seat_count == null ? '' : String(body.seat_count),
    term_years: body.term_years == null ? '' : String(body.term_years),
    notes: (body.notes ?? '') as string,
  });

  const save = useMutation(async () =>
    api(`/v1/governance/bodies/${body.code}`, {
      method: 'PATCH',
      body: {
        name: form.name.trim(),
        // Empty means "no fixed number", which is a real answer and not
        // the same as leaving it alone — so null rather than undefined.
        seat_count: form.seat_count === '' ? null : Number(form.seat_count),
        term_years: form.term_years === '' ? null : Number(form.term_years),
        notes: form.notes.trim() === '' ? null : form.notes.trim(),
      },
    }),
  );

  const retire = useMutation(async (active: boolean) =>
    api(`/v1/governance/bodies/${body.code}`, { method: 'PATCH', body: { is_active: active } }),
  );

  const remove = useMutation(async () =>
    api(`/v1/governance/bodies/${body.code}`, { method: 'DELETE' }),
  );

  if (!editing) {
    return (
      <div className="row" style={{ gap: 8 }}>
        <button className="secondary small" onClick={() => setEditing(true)}>
          Edit
        </button>
        {!body.is_permanent && (
          <button
            className="secondary small"
            disabled={retire.busy}
            onClick={async () => {
              await retire.run(!body.is_active);
              onChanged();
            }}
          >
            {body.is_active ? 'Retire' : 'Bring back'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <ErrorBanner error={save.error ?? retire.error ?? remove.error} />
      <div className="grid-2">
        <label className="field">
          <span>Name</span>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <div className="hint">
            The code stays {body.code}. Renaming a committee renames it; it does not replace it.
          </div>
        </label>
        <label className="field">
          <span>Seats</span>
          <input
            type="number"
            min={1}
            value={form.seat_count}
            onChange={(e) => setForm((f) => ({ ...f, seat_count: e.target.value }))}
            placeholder="no fixed number"
          />
        </label>
        <label className="field">
          <span>Term length, in years</span>
          <input
            type="number"
            min={1}
            value={form.term_years}
            onChange={(e) => setForm((f) => ({ ...f, term_years: e.target.value }))}
            placeholder="not set"
          />
        </label>
        <label className="field">
          <span>What it does</span>
          <input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
        </label>
      </div>
      <div className="actions">
        <button
          disabled={save.busy || form.name.trim().length < 2}
          onClick={async () => {
            const r = await save.run();
            if (r) {
              setEditing(false);
              onChanged();
            }
          }}
        >
          {save.busy ? 'Saving…' : 'Save'}
        </button>
        <button className="secondary" onClick={() => setEditing(false)}>
          Cancel
        </button>
        <div className="spacer" />
        {!body.is_permanent && (
          <button
            className="danger small"
            disabled={remove.busy}
            onClick={async () => {
              const r = await remove.run();
              if (r) onChanged();
            }}
          >
            {remove.busy ? 'Deleting…' : 'Delete'}
          </button>
        )}
      </div>
      {!body.is_permanent && (
        <p className="muted small" style={{ marginBottom: 0 }}>
          Deleting is refused once anybody has served on this or it has held a meeting. Retire it
          instead — it keeps its history and can be brought back.
        </p>
      )}
    </div>
  );
}

function NewCommittee({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', seat_count: '', term_years: '', notes: '' });

  const create = useMutation(async () =>
    api('/v1/governance/bodies', {
      method: 'POST',
      body: {
        name: form.name.trim(),
        seat_count: form.seat_count === '' ? undefined : Number(form.seat_count),
        term_years: form.term_years === '' ? undefined : Number(form.term_years),
        notes: form.notes.trim() || undefined,
      },
    }),
  );

  if (!open) {
    return (
      <Card>
        <div className="row" style={{ alignItems: 'baseline', gap: 12 }}>
          <button onClick={() => setOpen(true)}>New committee</button>
          <span className="muted small">
            Finance, Pool, Greens, House, Membership — whatever this club actually has.
          </span>
        </div>
      </Card>
    );
  }

  return (
    <Card title="A New Committee">
      <ErrorBanner error={create.error} />
      <div className="grid-2">
        <label className="field">
          <span>Name</span>
          <input
            autoFocus
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Pool Committee"
          />
        </label>
        <label className="field">
          <span>Seats</span>
          <input
            type="number"
            min={1}
            value={form.seat_count}
            onChange={(e) => setForm((f) => ({ ...f, seat_count: e.target.value }))}
            placeholder="no fixed number"
          />
          <div className="hint">Shown as a count. Nobody is refused for being the seventh.</div>
        </label>
        <label className="field">
          <span>Term length, in years</span>
          <input
            type="number"
            min={1}
            value={form.term_years}
            onChange={(e) => setForm((f) => ({ ...f, term_years: e.target.value }))}
            placeholder="not set"
          />
        </label>
        <label className="field">
          <span>What it does</span>
          <input
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Pool operations, staffing and the summer schedule"
          />
        </label>
      </div>
      <div className="actions">
        <button
          disabled={create.busy || form.name.trim().length < 2}
          onClick={async () => {
            const r = await create.run();
            if (r) {
              setForm({ name: '', seat_count: '', term_years: '', notes: '' });
              setOpen(false);
              onDone();
            }
          }}
        >
          {create.busy ? 'Creating…' : 'Create it'}
        </button>
        <button className="secondary" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <span className="muted small">It gets a Chair. Add other offices afterwards.</span>
      </div>
    </Card>
  );
}

export function BoardPage() {
  const { atLeast } = useSession();
  const bodies = useApi<{ bodies: any[] }>('/v1/governance/bodies');
  const [showPast, setShowPast] = useState(false);
  const roster = useApi<{ roster: any[] }>(
    `/v1/governance/roster${showPast ? '?all=true' : ''}`,
    [showPast],
  );

  const byBody = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const r of roster.data?.roster ?? []) {
      if (!m.has(r.body_code)) m.set(r.body_code, []);
      m.get(r.body_code)!.push(r);
    }
    return m;
  }, [roster.data]);

  return (
    <>
      <div className="row" style={{ marginBottom: 16, alignItems: 'baseline', gap: 12 }}>
        <h1>The Board and Its Committees</h1>
        <div className="spacer" />
        <label className="row" style={{ gap: 6, alignItems: 'center', marginBottom: 0 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', flex: '0 0 auto' }}
            checked={showPast}
            onChange={(e) => setShowPast(e.target.checked)}
          />
          <span className="small">Include past terms</span>
        </label>
      </div>

      <ErrorBanner error={bodies.error} />
      <ErrorBanner error={roster.error} />

      {/* Who may stand, how long they serve, what the cap is. Shown
          beside the seats rather than filed under Settings. */}
      <BylawNotices surface="governance.election" />

      {(bodies.data?.bodies ?? []).map((b) => {
        const members = byBody.get(b.code) ?? [];
        const filled = members.filter((m) => m.is_current).length;

        return (
          <Card
            key={b.id}
            title={
              <h2>
                {b.name}
                {!b.is_active && <span className="muted small"> · retired</span>}
              </h2>
            }
            actions={
              <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                <span className="muted small">
                  {b.seat_count ? `${filled} of ${b.seat_count} seats` : `${filled} members`}
                  {b.term_years ? ` · ${b.term_years}-year terms` : ''}
                  {b.term_limit_years ? ` · ${b.term_limit_years}-year cap` : ''}
                </span>
                {atLeast('club_admin') && (
                  <BodyControls body={b} onChanged={() => { bodies.reload(); roster.reload(); }} />
                )}
              </div>
            }
          >
            {b.notes && (
              <p className="muted small" style={{ marginTop: 0 }}>
                {b.notes}
                {b.authority_path && <> ({b.authority_path})</>}
              </p>
            )}

            {members.length === 0 ? (
              <Empty>Nobody recorded on this yet.</Empty>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <th>Who</th>
                    <th>Office</th>
                    <th>From</th>
                    <th>Until</th>
                    <th className="num">Served</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const near =
                      b.term_limit_years &&
                      Number(m.consecutive_years) >= b.term_limit_years - 1.5;
                    return (
                      <tr key={m.term_id} className={m.is_current ? undefined : 'muted'}>
                        <td>
                          {m.name}
                          {m.filled_vacancy && (
                            <span className="muted small"> · filling a vacancy</span>
                          )}
                        </td>
                        <td>
                          {m.position_name ? (
                            <strong>{m.position_name}</strong>
                          ) : (
                            <span className="muted small">Governor</span>
                          )}
                        </td>
                        <td className="small">{fmtDate(m.starts_on)}</td>
                        <td className="small">
                          {m.ends_on ? fmtDate(m.ends_on) : <span className="muted">open</span>}
                          {/* A term ending inside three months is the whole
                              reason anybody opens this page in the autumn. */}
                          {m.is_current &&
                            m.days_until_end !== null &&
                            m.days_until_end <= 90 && (
                              <div className={m.days_until_end <= 30 ? 'neg' : 'muted'}>
                                {m.days_until_end <= 0
                                  ? 'ends today'
                                  : `in ${m.days_until_end} days`}
                              </div>
                            )}
                        </td>
                        <td className={near ? 'num neg' : 'num'}>
                          {Number(m.consecutive_years).toFixed(1)} yrs
                          {near && <div className="small">near the cap</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
        );
      })}

      {atLeast('club_admin') && (
        <>
          <ElectSomebody
            onDone={() => { roster.reload(); bodies.reload(); }}
            bodies={(bodies.data?.bodies ?? []).filter((b) => b.is_active)}
          />
          <NewCommittee onDone={() => bodies.reload()} />
        </>
      )}
    </>
  );
}

function ElectSomebody({ bodies, onDone }: { bodies: any[]; onDone: () => void }) {
  const [form, setForm] = useState({
    body_code: 'BOARD',
    position_code: '',
    person_id: '',
    starts_on: new Date().toISOString().slice(0, 10),
    ends_on: '',
  });
  const [q, setQ] = useState('');
  const people = useApi<{ people: any[] }>(
    `/v1/governance/eligible?q=${encodeURIComponent(q)}`,
    [q],
  );
  const positions = useApi<{ positions: any[] }>(
    `/v1/governance/bodies/${form.body_code}/positions`,
    [form.body_code],
  );

  const elect = useMutation(async () =>
    api('/v1/governance/terms', {
      method: 'POST',
      body: {
        body_code: form.body_code,
        position_code: form.position_code || undefined,
        person_id: form.person_id,
        starts_on: form.starts_on,
        ends_on: form.ends_on || undefined,
      },
    }),
  );

  return (
    <Card title="Record an Election or Appointment">
      <ErrorBanner error={elect.error} />
      <div className="grid-2">
        <label className="field">
          <span>To</span>
          <select
            value={form.body_code}
            // The office codes belong to the body. Carrying one across to
            // a different committee asks the API for an office that
            // committee does not have, and gets a 404 nobody can read.
            onChange={(e) => setForm((f) => ({ ...f, body_code: e.target.value, position_code: '' }))}
          >
            {bodies.map((b) => (
              <option key={b.code} value={b.code}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Office, if any</span>
          {/* Was a free-text code somebody had to know. Once a club can
              make its own committees, nobody knows what POOL_COMMITTEE's
              chair is called, so the offices come from the body. */}
          <select
            value={form.position_code}
            onChange={(e) => setForm((f) => ({ ...f, position_code: e.target.value }))}
          >
            <option value="">No office — an ordinary member of it</option>
            {(positions.data?.positions ?? []).map((p: any) => (
              <option key={p.code} value={p.code}>
                {p.name}
                {p.held_now > 0 ? ' — currently held' : ''}
              </option>
            ))}
          </select>
          <div className="hint">
            An office already held will be refused unless that term is ended first.
          </div>
        </label>
        <label className="field">
          <span>Who</span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name"
            style={{ marginBottom: 6 }}
          />
          <select
            value={form.person_id}
            onChange={(e) => setForm((f) => ({ ...f, person_id: e.target.value }))}
            size={Math.min(6, Math.max(2, (people.data?.people ?? []).length))}
          >
            {(people.data?.people ?? []).map((p: any) => (
              <option key={p.id} value={p.id}>
                {p.label}
                {p.joined_on ? ` — member ${p.years_a_member} yrs` : ' — not a member'}
              </option>
            ))}
          </select>
          <div className="hint">
            Article III B 2 asks for five years' membership before election to the Board.
          </div>
        </label>
        <label className="field">
          <span>Term</span>
          <div className="row" style={{ gap: 8 }}>
            <input
              type="date"
              value={form.starts_on}
              onChange={(e) => setForm((f) => ({ ...f, starts_on: e.target.value }))}
            />
            <input
              type="date"
              value={form.ends_on}
              onChange={(e) => setForm((f) => ({ ...f, ends_on: e.target.value }))}
            />
          </div>
        </label>
      </div>
      <div className="actions">
        <button
          disabled={!form.person_id || elect.busy}
          onClick={async () => {
            const r = await elect.run();
            if (r) onDone();
          }}
        >
          {elect.busy ? 'Recording…' : 'Record it'}
        </button>
        <span className="muted small">
          The twelve-year cap on consecutive service is enforced; a term that would breach it is
          refused.
        </span>
      </div>
    </Card>
  );
}

// =====================================================================
// Meetings
// =====================================================================

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKS = [
  { v: 1, label: 'first' }, { v: 2, label: 'second' }, { v: 3, label: 'third' },
  { v: 4, label: 'fourth' }, { v: -1, label: 'last' },
];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function kindName(k: string) {
  return k === 'board' ? 'The Board'
    : k === 'annual' ? 'Annual meeting'
    : k === 'special' ? 'Special meeting'
    : 'Committee';
}

function ordinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

/** The rule, as a sentence. A club should recognize its own calendar. */
function cadenceSentence(s: any): string {
  if (s.cadence === 'weekly') {
    return s.interval_count > 1 ? `Every ${s.interval_count} weeks` : 'Every week';
  }
  const when =
    s.weekday !== null && s.weekday !== undefined
      ? `the ${WEEKS.find((w) => w.v === s.week_of_month)?.label ?? ''} ${WEEKDAYS[s.weekday]}`
      : s.day_of_month
        ? `the ${ordinal(s.day_of_month)}`
        : 'the same date';
  const unit = s.cadence === 'annual' ? 'year' : s.cadence === 'quarterly' ? 'quarter' : 'month';
  const every = s.interval_count > 1 ? `every ${s.interval_count} ${unit}s` : `every ${unit}`;
  if (s.cadence === 'annual' && s.month_of_year) {
    return `${when} of ${MONTHS[s.month_of_year - 1]}, ${every}`;
  }
  return `${when} of ${every}`;
}

/**
 * A meeting that comes round again.
 *
 * The cadence is not editable once a series exists, and that is
 * deliberate rather than unfinished: meetings already generated do not
 * move, so changing "third Tuesday" to "first Monday" would leave a
 * calendar half under one rule and half under the other, with nothing on
 * screen saying which is which. End the series and start another.
 */
function SeriesCard({ bodies, onChanged }: { bodies: any[]; onChanged: () => void }) {
  const { atLeast } = useSession();
  const series = useApi<{ series: any[] }>('/v1/governance/series');
  const [open, setOpen] = useState(false);
  const nextYear = new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: '', kind: 'annual', body_code: '', cadence: 'monthly',
    weekday: '2', week_of_month: '3', day_of_month: '', month_of_year: '',
    by: 'weekday' as 'weekday' | 'date',
    starts_on: new Date().toISOString().slice(0, 10),
    default_location: '', default_join_url: '',
  });

  const create = useMutation(async () =>
    api('/v1/governance/series', {
      method: 'POST',
      body: {
        name: form.name.trim(),
        kind: form.kind,
        body_code: form.body_code || undefined,
        cadence: form.cadence,
        weekday: form.cadence !== 'weekly' && form.by === 'weekday' ? Number(form.weekday) : undefined,
        week_of_month: form.cadence !== 'weekly' && form.by === 'weekday' ? Number(form.week_of_month) : undefined,
        day_of_month: form.cadence !== 'weekly' && form.by === 'date' && form.day_of_month
          ? Number(form.day_of_month) : undefined,
        month_of_year: form.cadence === 'annual' && form.month_of_year
          ? Number(form.month_of_year) : undefined,
        starts_on: form.starts_on,
        default_location: form.default_location.trim() || undefined,
        default_join_url: form.default_join_url.trim() || undefined,
        // A calendar nobody can see is not a calendar.
        generate_through: nextYear,
      },
    }),
  );

  const generate = useMutation(async (id: string) =>
    api(`/v1/governance/series/${id}/generate`, { method: 'POST', body: { through: nextYear } }),
  );

  const retire = useMutation(async (id: string) =>
    api(`/v1/governance/series/${id}`, { method: 'PATCH', body: { is_active: false } }),
  );

  const rows = series.data?.series ?? [];

  return (
    <Card
      title="Meetings That Come Round Again"
      actions={
        atLeast('manager') && !open
          ? <button className="secondary small" onClick={() => setOpen(true)}>New series</button>
          : null
      }
    >
      <ErrorBanner error={series.error ?? create.error ?? generate.error ?? retire.error} />

      {rows.length === 0 && !open ? (
        <Empty>
          Nothing recurring yet. A club's governance year is mostly known in advance — the Board
          on the third Tuesday, the annual meeting in April — and this is where it gets written
          down once instead of every month.
        </Empty>
      ) : rows.length > 0 ? (
        <table className="data">
          <thead>
            <tr>
              <th>What</th>
              <th>When</th>
              <th>Next</th>
              <th className="num">Scheduled</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((s: any) => (
              <tr key={s.id} className={s.is_active ? undefined : 'muted'}>
                <td>
                  <strong>{s.name}</strong>
                  <div className="muted small">
                    {s.body_name ?? kindName(s.kind)}
                    {!s.is_active && ' · ended'}
                  </div>
                </td>
                <td className="small">{cadenceSentence(s)}</td>
                <td className="small">
                  {s.next_on ? fmtDate(s.next_on) : <span className="muted">none ahead</span>}
                </td>
                <td className="num">{s.generated}</td>
                <td className="num">
                  {atLeast('manager') && s.is_active && (
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        className="secondary small"
                        disabled={generate.busy}
                        onClick={async () => {
                          await generate.run(s.id);
                          series.reload();
                          onChanged();
                        }}
                      >
                        Fill a year
                      </button>
                      <button
                        className="secondary small"
                        disabled={retire.busy}
                        onClick={async () => {
                          await retire.run(s.id);
                          series.reload();
                        }}
                      >
                        End it
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {open && (
        <div style={{ marginTop: rows.length > 0 ? 18 : 0 }}>
          <div className="grid-2">
            <label className="field">
              <span>Name</span>
              <input
                autoFocus
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Regular meeting of the Board"
              />
            </label>
            <label className="field">
              <span>Whose meeting</span>
              <select
                value={form.body_code === '' ? form.kind : form.body_code}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'annual' || v === 'special') {
                    setForm((f) => ({ ...f, kind: v, body_code: '' }));
                  } else {
                    const b = bodies.find((x) => x.code === v);
                    setForm((f) => ({
                      ...f, body_code: v, kind: b?.kind === 'board' ? 'board' : 'committee',
                    }));
                  }
                }}
              >
                <option value="annual">The membership — annual meeting</option>
                <option value="special">The membership — special meeting</option>
                {bodies.filter((b) => b.is_active).map((b) => (
                  <option key={b.code} value={b.code}>{b.name}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>How often</span>
              <select
                value={form.cadence}
                onChange={(e) => setForm((f) => ({ ...f, cadence: e.target.value }))}
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Once a year</option>
              </select>
            </label>
            {form.cadence !== 'weekly' && (
              <label className="field">
                <span>Which day</span>
                <div className="row" style={{ gap: 8 }}>
                  <select
                    value={form.by}
                    onChange={(e) => setForm((f) => ({ ...f, by: e.target.value as 'weekday' | 'date' }))}
                  >
                    <option value="weekday">A weekday</option>
                    <option value="date">A date</option>
                  </select>
                  {form.by === 'weekday' ? (
                    <>
                      <select
                        value={form.week_of_month}
                        onChange={(e) => setForm((f) => ({ ...f, week_of_month: e.target.value }))}
                      >
                        {WEEKS.map((w) => <option key={w.v} value={w.v}>{w.label}</option>)}
                      </select>
                      <select
                        value={form.weekday}
                        onChange={(e) => setForm((f) => ({ ...f, weekday: e.target.value }))}
                      >
                        {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                      </select>
                    </>
                  ) : (
                    <input
                      type="number"
                      min={1}
                      max={31}
                      value={form.day_of_month}
                      onChange={(e) => setForm((f) => ({ ...f, day_of_month: e.target.value }))}
                      placeholder="15"
                    />
                  )}
                  {form.cadence === 'annual' && (
                    <select
                      value={form.month_of_year}
                      onChange={(e) => setForm((f) => ({ ...f, month_of_year: e.target.value }))}
                    >
                      <option value="">the starting month</option>
                      {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  )}
                </div>
                <div className="hint">
                  A 31st in a short month lands on the last day rather than rolling into the next.
                </div>
              </label>
            )}
            <label className="field">
              <span>Starting</span>
              <input
                type="date"
                value={form.starts_on}
                onChange={(e) => setForm((f) => ({ ...f, starts_on: e.target.value }))}
              />
            </label>
            <label className="field">
              <span>Where</span>
              <input
                value={form.default_location}
                onChange={(e) => setForm((f) => ({ ...f, default_location: e.target.value }))}
                placeholder="The boardroom"
              />
            </label>
            <label className="field">
              <span>Join link</span>
              <input
                value={form.default_join_url}
                onChange={(e) => setForm((f) => ({ ...f, default_join_url: e.target.value }))}
                placeholder="https://zoom.us/j/…"
              />
              <div className="hint">
                Pasted, not created. Schedule it in Zoom or Teams and put the address here.
              </div>
            </label>
          </div>
          <div className="actions">
            <button
              disabled={create.busy || form.name.trim().length < 2}
              onClick={async () => {
                const r = await create.run();
                if (r) {
                  setOpen(false);
                  setForm((f) => ({ ...f, name: '', default_location: '', default_join_url: '' }));
                  series.reload();
                  onChanged();
                }
              }}
            >
              {create.busy ? 'Creating…' : 'Create it and fill a year'}
            </button>
            <button className="secondary" onClick={() => setOpen(false)}>Cancel</button>
            <span className="muted small">
              The cadence cannot be changed afterwards — meetings already scheduled do not move.
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}

export function MeetingsPage() {
  const { atLeast } = useSession();
  const { data, error, loading, reload } = useApi<{ meetings: any[] }>('/v1/governance/meetings');
  const bodies = useApi<{ bodies: any[] }>('/v1/governance/bodies');
  const [open, setOpen] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const [moveTo, setMoveTo] = useState('');

  const move = useMutation(async (id: string, to: string) =>
    api(`/v1/governance/meetings/${id}/move`, { method: 'POST', body: { to } }),
  );

  const today = new Date().toISOString().slice(0, 10);
  const all = data?.meetings ?? [];
  const isAhead = (m: any) => m.held_on >= today && m.status === 'scheduled';
  const upcoming = all.filter(isAhead).sort((a, b) => a.held_on.localeCompare(b.held_on));
  const past = all.filter((m) => !isAhead(m));

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Meetings</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        With the quorum answered rather than assumed. Nothing carries at a meeting that did not
        have the numbers, and a motion recorded as carried when its own tally says otherwise is
        refused.
      </p>

      <ErrorBanner error={error} />
      <ErrorBanner error={move.error} />

      {/* Notice periods, quorum, the thresholds a motion has to clear. */}
      <BylawNotices surface="governance.meeting" />

      <SeriesCard bodies={bodies.data?.bodies ?? []} onChanged={reload} />

      <Card title="Coming Up">
        {loading ? (
          <Loading />
        ) : upcoming.length === 0 ? (
          <Empty>Nothing scheduled. A series above will fill the calendar.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>Where</th>
                <th className="num">Asked</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {upcoming.map((m) => (
                <tr key={m.id}>
                  <td onClick={() => setOpen(open === m.id ? null : m.id)} style={{ cursor: 'pointer' }}>
                    <strong>{fmtDate(m.held_on)}</strong>
                    {/* Where it was meant to be. A club looking at a
                        Wednesday board meeting wants to know that was the
                        holiday and not a mistake. */}
                    {m.moved_from && (
                      <div className="muted small">moved from {fmtDate(m.moved_from)}</div>
                    )}
                  </td>
                  <td onClick={() => setOpen(open === m.id ? null : m.id)} style={{ cursor: 'pointer' }}>
                    {m.title ?? m.body_name ?? kindName(m.kind)}
                    {m.notes_moved && <div className="muted small">{m.notes_moved}</div>}
                  </td>
                  <td className="small">
                    {m.location ?? <span className="muted">—</span>}
                    {m.join_url && (
                      <div><a href={m.join_url} target="_blank" rel="noreferrer">join</a></div>
                    )}
                  </td>
                  <td className="num">
                    {m.invite_count === 0
                      ? <span className="muted">nobody</span>
                      : String(m.invitee_count)}
                  </td>
                  <td className="num">
                    {atLeast('manager') && (
                      moving === m.id ? (
                        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <input
                            type="date"
                            value={moveTo}
                            onChange={(e) => setMoveTo(e.target.value)}
                            style={{ width: 160 }}
                          />
                          <button
                            className="small"
                            disabled={!moveTo || move.busy}
                            onClick={async () => {
                              const r = await move.run(m.id, moveTo);
                              if (r) { setMoving(null); reload(); }
                            }}
                          >
                            Move
                          </button>
                          <button className="secondary small" onClick={() => setMoving(null)}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="secondary small"
                          onClick={() => { setMoving(m.id); setMoveTo(m.held_on); }}
                        >
                          Move
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="What Has Been Held">
        {loading ? (
          <Loading />
        ) : past.length === 0 ? (
          <Empty>Nothing held yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>Notice</th>
                <th>Turnout</th>
                <th>Quorum</th>
                <th className="num">Motions</th>
              </tr>
            </thead>
            <tbody>
              {past.map((m) => (
                <tr
                  key={m.id}
                  onClick={() => setOpen(open === m.id ? null : m.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{fmtDate(m.held_on)}</td>
                  <td>
                    {m.title ?? m.body_name ?? kindName(m.kind)}
                    {m.location && <div className="muted small">{m.location}</div>}
                  </td>
                  <td className="small">
                    {m.notice_days === null ? (
                      <span className="muted">—</span>
                    ) : (
                      <span className={m.notice_days < 5 && m.kind !== 'board' ? 'neg' : undefined}>
                        {m.notice_days} days
                      </span>
                    )}
                  </td>
                  <td className="small">
                    {m.attendee_count ?? '—'}
                    {m.proxy_count > 0 && (
                      <span className="muted"> + {m.proxy_count} proxies</span>
                    )}
                  </td>
                  <td>
                    {m.had_quorum === null ? (
                      <span className="muted small">not recorded</span>
                    ) : m.had_quorum ? (
                      <Status tone="good">met ({m.quorum_required})</Status>
                    ) : (
                      <Status tone="critical">short of {m.quorum_required}</Status>
                    )}
                  </td>
                  <td className="num">{m.motion_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {open && <MeetingDetail id={open} onChanged={reload} />}
    </>
  );
}

function MeetingDetail({ id, onChanged }: { id: string; onChanged: () => void }) {
  const { atLeast } = useSession();
  const { data, error, loading, reload } = useApi<any>(`/v1/governance/meetings/${id}`);
  const [form, setForm] = useState({
    subject: '',
    cite: '',
    threshold: 'majority',
    of_full_body: false,
    votes_for: '',
    votes_against: '',
    votes_abstain: '',
  });

  const record = useMutation(async () =>
    api(`/v1/governance/meetings/${id}/motions`, {
      method: 'POST',
      body: {
        subject: form.subject.trim(),
        cite: form.cite.trim() || undefined,
        threshold: form.threshold,
        of_full_body: form.of_full_body,
        votes_for: form.votes_for === '' ? undefined : Number(form.votes_for),
        votes_against: form.votes_against === '' ? undefined : Number(form.votes_against),
        votes_abstain: form.votes_abstain === '' ? undefined : Number(form.votes_abstain),
      },
    }),
  );

  if (loading) return <Loading rows={2} />;
  if (error) return <ErrorBanner error={error} />;

  return (
    <Card title={`What was decided — ${fmtDate(data?.meeting?.held_on)}`}>
      <ErrorBanner error={record.error} />

      {(data?.motions ?? []).length === 0 ? (
        <Empty>No motions recorded at this meeting.</Empty>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Motion</th>
              <th>Under</th>
              <th>Needed</th>
              <th>For</th>
              <th>Against</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {data.motions.map((m: any) => (
              <tr key={m.id}>
                <td>
                  {m.subject}
                  {m.detail && <div className="muted small">{m.detail}</div>}
                </td>
                <td className="mono small">{m.cite ?? '—'}</td>
                <td className="small muted">
                  {m.threshold.replace(/_/g, ' ')}
                  {m.of_full_body && <div>of the full body</div>}
                </td>
                <td className="num">{m.votes_for ?? '—'}</td>
                <td className="num">{m.votes_against ?? '—'}</td>
                <td>
                  {m.carried === null ? (
                    <span className="muted small">—</span>
                  ) : (
                    <Status tone={m.carried ? 'good' : 'critical'}>
                      {m.carried ? 'carried' : 'failed'}
                    </Status>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data?.minutes && (
        <div style={{ marginTop: 14 }}>
          <div className="tile__label">Minutes</div>
          <p className="bylaw-body">{data.minutes}</p>
        </div>
      )}

      {atLeast('manager') && data?.meeting?.status === 'held' && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--hairline)', paddingTop: 14 }}>
          <div className="grid-2">
            <label className="field">
              <span>Motion</span>
              <input
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="A capital assessment of $1,200 per Voting Member"
              />
            </label>
            <label className="field">
              <span>Taken under</span>
              <input
                value={form.cite}
                onChange={(e) => setForm((f) => ({ ...f, cite: e.target.value }))}
                placeholder="III.I"
              />
              <div className="hint">The bylaw section that authorizes it.</div>
            </label>
          </div>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label className="field" style={{ marginBottom: 0 }}>
              <span>Needs</span>
              <select
                value={form.threshold}
                onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))}
                style={{ width: 'auto' }}
              >
                <option value="majority">A majority</option>
                <option value="two_thirds">Two thirds</option>
                <option value="unanimous">Unanimous</option>
              </select>
            </label>
            <label className="field" style={{ marginBottom: 0, maxWidth: 90 }}>
              <span>For</span>
              <input
                value={form.votes_for}
                onChange={(e) => setForm((f) => ({ ...f, votes_for: e.target.value }))}
              />
            </label>
            <label className="field" style={{ marginBottom: 0, maxWidth: 90 }}>
              <span>Against</span>
              <input
                value={form.votes_against}
                onChange={(e) => setForm((f) => ({ ...f, votes_against: e.target.value }))}
              />
            </label>
            <label className="field" style={{ marginBottom: 0, maxWidth: 90 }}>
              <span>Abstain</span>
              <input
                value={form.votes_abstain}
                onChange={(e) => setForm((f) => ({ ...f, votes_abstain: e.target.value }))}
              />
            </label>
            <button
              disabled={!form.subject.trim() || record.busy}
              onClick={async () => {
                const r = await record.run();
                if (r) {
                  setForm((f) => ({ ...f, subject: '', cite: '', votes_for: '', votes_against: '', votes_abstain: '' }));
                  reload();
                  onChanged();
                }
              }}
            >
              Record it
            </button>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            The result is worked out from the tally. Leave the votes blank if the minute did not
            record them.
          </div>
        </div>
      )}

      <Invitees meeting={data?.meeting} invites={data?.invites ?? []}
                invitees={data?.invitees ?? []} onChanged={() => { reload(); onChanged(); }} />
      <Minutes meeting={data?.meeting} minutes={data?.minutes ?? null}
               onChanged={() => { reload(); onChanged(); }} />
    </Card>
  );
}

/**
 * Who was asked, and who that turns out to be.
 *
 * Both are shown, because they are different facts. "The Board" is the
 * decision somebody made; the eleven names are what it means this week,
 * and it will mean something else after the April election. Storing the
 * names instead would freeze the second and lose the first.
 */
function Invitees(
  { meeting, invites, invitees, onChanged }:
  { meeting: any; invites: any[]; invitees: any[]; onChanged: () => void },
) {
  const { atLeast } = useSession();
  const bodies = useApi<{ bodies: any[] }>('/v1/governance/bodies');
  const [audience, setAudience] = useState('body');
  const [bodyCode, setBodyCode] = useState('');
  const [q, setQ] = useState('');
  const [personId, setPersonId] = useState('');
  const people = useApi<{ people: any[] }>(
    // null rather than '' so the hook does not have to interpret an
    // empty string as "do not ask".
    audience === 'person' ? `/v1/governance/eligible?q=${encodeURIComponent(q)}` : null,
    [audience, q],
  );

  const invite = useMutation(async () =>
    api(`/v1/governance/meetings/${meeting.id}/invites`, {
      method: 'POST',
      body: {
        audience,
        body_code: audience === 'body' ? bodyCode || undefined : undefined,
        person_id: audience === 'person' ? personId : undefined,
      },
    }),
  );

  const uninvite = useMutation(async (inviteId: string) =>
    api(`/v1/governance/invites/${inviteId}`, { method: 'DELETE' }),
  );

  if (!meeting) return null;
  const list = bodies.data?.bodies ?? [];

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ marginBottom: 8 }}>Who is asked</h3>
      <ErrorBanner error={invite.error ?? uninvite.error} />

      {invites.length === 0 ? (
        <Empty>Nobody has been asked to this yet.</Empty>
      ) : (
        <ul className="pairs" style={{ display: 'block', marginBottom: 12 }}>
          {invites.map((i) => (
            <li key={i.id} className="row" style={{ gap: 10, alignItems: 'baseline' }}>
              <span>
                {i.audience === 'body' ? i.body_name
                  : i.audience === 'all_members' ? 'Every member of the club'
                  : i.audience === 'staff' ? 'The club\u2019s staff'
                  : i.audience === 'vendor' ? i.vendor_name
                  : i.person_name}
              </span>
              {i.note && <span className="muted small">{i.note}</span>}
              <div className="spacer" />
              {atLeast('manager') && (
                <button
                  className="secondary small"
                  disabled={uninvite.busy}
                  onClick={async () => { await uninvite.run(i.id); onChanged(); }}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {invitees.length > 0 && (
        <details style={{ marginBottom: 12 }}>
          <summary className="small">
            {invitees.length} {invitees.length === 1 ? 'person' : 'people'}, worked out today
          </summary>
          <table className="data" style={{ marginTop: 8 }}>
            <tbody>
              {invitees.map((p, n) => (
                <tr key={p.person_id ?? `v${n}`}>
                  <td>{p.display_name}</td>
                  <td className="muted small">{p.why}</td>
                  <td className="muted small">{p.email ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {atLeast('manager') && (
        <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
          <select value={audience} onChange={(e) => setAudience(e.target.value)}>
            <option value="body">A body</option>
            <option value="all_members">Every member</option>
            <option value="staff">The staff</option>
            <option value="person">One person</option>
          </select>
          {audience === 'body' && (
            <select value={bodyCode} onChange={(e) => setBodyCode(e.target.value)}>
              <option value="">Which…</option>
              {list.filter((b: any) => b.is_active).map((b: any) => (
                <option key={b.code} value={b.code}>{b.name}</option>
              ))}
            </select>
          )}
          {audience === 'person' && (
            <>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name"
                style={{ maxWidth: 180 }}
              />
              <select value={personId} onChange={(e) => setPersonId(e.target.value)}>
                <option value="">Who…</option>
                {(people.data?.people ?? []).map((p: any) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </>
          )}
          <button
            disabled={
              invite.busy ||
              (audience === 'body' && !bodyCode) ||
              (audience === 'person' && !personId)
            }
            onClick={async () => {
              const r = await invite.run();
              if (r) { setBodyCode(''); setPersonId(''); onChanged(); }
            }}
          >
            Ask them
          </button>
        </div>
      )}
      <div className="hint" style={{ marginTop: 6 }}>
        &ldquo;Every member&rdquo; stays true as the roster changes — somebody who joins tomorrow
        is asked to the annual meeting without anybody editing this.
      </div>
    </div>
  );
}

/**
 * The minutes.
 *
 * Typed here or pasted from wherever they were written. Saving replaces
 * what was there — there is no version history yet, and that is a gap
 * worth naming rather than papering over: minutes are read years later
 * by somebody settling an argument, and "who changed this and when"
 * currently has an answer only in the audit log.
 */
function Minutes(
  { meeting, minutes, onChanged }:
  { meeting: any; minutes: string | null; onChanged: () => void },
) {
  const { atLeast } = useSession();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(minutes ?? '');

  const save = useMutation(async () =>
    api(`/v1/governance/meetings/${meeting.id}`, {
      method: 'PATCH',
      body: { minutes: text.trim() === '' ? null : text },
    }),
  );

  if (!meeting) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <div className="row" style={{ alignItems: 'baseline', gap: 10 }}>
        <h3 style={{ marginBottom: 8 }}>Minutes</h3>
        <div className="spacer" />
        {atLeast('manager') && !editing && (
          <button
            className="secondary small"
            onClick={() => { setText(minutes ?? ''); setEditing(true); }}
          >
            {minutes ? 'Edit' : 'Write them'}
          </button>
        )}
      </div>
      <ErrorBanner error={save.error} />

      {editing ? (
        <>
          <textarea
            rows={14}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Present: … The meeting was called to order at …"
            style={{ width: '100%', fontFamily: 'inherit' }}
          />
          <div className="actions">
            <button
              disabled={save.busy}
              onClick={async () => {
                const r = await save.run();
                if (r) { setEditing(false); onChanged(); }
              }}
            >
              {save.busy ? 'Saving…' : 'Save the minutes'}
            </button>
            <button className="secondary" onClick={() => setEditing(false)}>Cancel</button>
            <span className="muted small">
              Saving replaces what was there. The change is in the audit log; the previous text is
              not kept.
            </span>
          </div>
        </>
      ) : minutes ? (
        <p style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{minutes}</p>
      ) : (
        <Empty>No minutes recorded.</Empty>
      )}
    </div>
  );
}
